import { create } from 'zustand';
import { castDraft } from 'immer';
import { immer } from 'zustand/middleware/immer';
import type {
  AttachmentStatus,
  ConverseAttachment,
  PendingAttachment,
} from '../constants/fileUploadConstants';

// ---------------------------------------------------------------------------
// State & Actions
// ---------------------------------------------------------------------------

export interface EsaAttachmentState {
  attachments: PendingAttachment[];
}

export interface EsaAttachmentActions {
  /** Create PendingAttachment entries in 'pending' status. */
  addAttachments(
    files: Array<{
      localId: string;
      file: File;
      fileType: string;
      fileExtension: string;
    }>,
  ): void;

  /** Transition matching attachments to 'creating' status. */
  setCreating(localIds: string[]): void;

  /** Transition to 'uploading', assign FSS ukey to each. */
  setUploading(localIdToUkey: Record<string, string>): void;

  /** Store active XHR reference for abort support. */
  storeXhr(ukey: string, xhr: XMLHttpRequest): void;

  /** Clear XHR reference (on complete / abort / fail). */
  clearXhr(ukey: string): void;

  /** Update upload percentage (0–100) for a specific attachment. */
  updateProgress(ukey: string, percent: number): void;

  /** Transition to 'ready' — upload + status confirmation complete. */
  markReady(ukey: string): void;

  /**
   * Transition to 'failed'.
   * @param identifier localId or ukey — looks up by both.
   */
  markFailed(identifier: string, error: string): void;

  /** Remove from store array. Caller handles XHR abort + API delete. */
  removeAttachment(localId: string): void;

  /** Clear all attachments (after successful send). */
  clearAttachments(): void;

  /** Look up by localId. */
  getAttachment(localId: string): PendingAttachment | undefined;

  /** Return all 'ready' attachments formatted for the converse payload. */
  getReadyAttachments(): ConverseAttachment[];

  /** True when attachments.length > 0 and all have status 'ready'. */
  allAttachmentsReady(): boolean;

  /** True when attachments.length > 0. */
  hasAttachments(): boolean;

  /** True when any attachment is in 'pending', 'creating', or 'uploading'. */
  hasInFlightUploads(): boolean;
}

const IN_FLIGHT_STATUSES: ReadonlySet<AttachmentStatus> = new Set([
  'pending',
  'creating',
  'uploading',
]);

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const useEsaAttachmentStore = create<EsaAttachmentState & EsaAttachmentActions>()(
  immer((set, get) => ({
    attachments: [],

    addAttachments(files) {
      set(state => {
        for (const { localId, file, fileType, fileExtension } of files) {
          state.attachments.push({
            localId,
            ukey: null,
            fileName: file.name,
            fileType,
            fileExtension,
            sizeBytes: file.size,
            status: 'pending',
            progress: 0,
            error: null,
            file,
            xhr: null,
          });
        }
      });
    },

    setCreating(localIds) {
      const ids = new Set(localIds);
      set(state => {
        for (const att of state.attachments) {
          if (ids.has(att.localId)) att.status = 'creating';
        }
      });
    },

    setUploading(localIdToUkey) {
      set(state => {
        for (const att of state.attachments) {
          const ukey = localIdToUkey[att.localId];
          if (ukey !== undefined) {
            att.status = 'uploading';
            att.ukey = ukey;
          }
        }
      });
    },

    storeXhr(ukey, xhr) {
      set(state => {
        const att = state.attachments.find(a => a.ukey === ukey);
        // castDraft tells immer not to proxy the complex browser XMLHttpRequest type
        if (att) att.xhr = castDraft(xhr);
      });
    },

    clearXhr(ukey) {
      set(state => {
        const att = state.attachments.find(a => a.ukey === ukey);
        if (att) att.xhr = null;
      });
    },

    updateProgress(ukey, percent) {
      set(state => {
        const att = state.attachments.find(a => a.ukey === ukey);
        if (att) att.progress = percent;
      });
    },

    markReady(ukey) {
      set(state => {
        const att = state.attachments.find(a => a.ukey === ukey);
        if (att) {
          att.status = 'ready';
          att.progress = 100;
          att.xhr = null;
          att.error = null;
        }
      });
    },

    markFailed(identifier, error) {
      set(state => {
        const att =
          state.attachments.find(a => a.localId === identifier) ??
          state.attachments.find(a => a.ukey === identifier);
        if (att) {
          att.status = 'failed';
          att.error = error;
          att.xhr = null;
        }
      });
    },

    removeAttachment(localId) {
      set(state => {
        state.attachments = state.attachments.filter(a => a.localId !== localId);
      });
    },

    clearAttachments() {
      set(state => {
        state.attachments = [];
      });
    },

    getAttachment(localId) {
      return get().attachments.find(a => a.localId === localId);
    },

    getReadyAttachments() {
      return get()
        .attachments.filter(a => a.status === 'ready' && a.ukey !== null)
        .map(a => ({
          file_id: a.ukey as string,
          file_name: a.fileName,
          content_type: a.fileType,
        }));
    },

    allAttachmentsReady() {
      const { attachments } = get();
      return attachments.length > 0 && attachments.every(a => a.status === 'ready');
    },

    hasAttachments() {
      return get().attachments.length > 0;
    },

    hasInFlightUploads() {
      return get().attachments.some(a => IN_FLIGHT_STATUSES.has(a.status));
    },
  })),
);

// ---------------------------------------------------------------------------
// Test helper — reset to empty state
// ---------------------------------------------------------------------------

/** Reset store to initial state — for use in beforeEach in tests only. */
export function resetEsaAttachmentStore(): void {
  useEsaAttachmentStore.setState({ attachments: [] });
}
